import React, { Component } from "react";
import _ from "lodash";
import moment from "moment";
import Loader from "react-loader-spinner";
import axios from "axios";
import Paginator from "../../utils/paginator/Paginator";
import SortButton from "../SortButton";
import { determineNavStyling } from "../Nav/determineNavStyling";
import { config as appConfig } from "../../utils/conf";
import { requestHeaders } from "../../utils/http";
import ConfirmationModal from "../Modal/ConfirmationModal";

class BatchProcessingHistory extends Component {
  state = {
    sortedData: null,
    sortedColumn: "",
    currentPage: 1,
    perPage: null,
    headers: [
      { colTitle: "Date of upload", field: "submissionTime" },
      { colTitle: "Spreadsheet name", field: "bulkSubmissionArtifacts" },
      { colTitle: "Number of records found", field: "bulkSubmissionEntities" },
      {
        colTitle: "number processed successfully",
        field: "processedSuccessfully",
      },
      { colTitle: "Number of errors", field: "errorCount" },
    ],
    width: window.innerWidth,
    showErrorsModal: false,
    errorsModalRows: [],
    errorsModalFileName: "",
  };

  componentDidMount() {
    determineNavStyling(this.props.location.pathname);
    if (this.state.perPage === null && this.props.config?.defaultLimit) {
      this.setState({ perPage: this.props.config.defaultLimit });
    }
    this.fetchData();
    window.addEventListener("resize", this.updateDimensions);
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (
      prevProps.config?.defaultLimit !== this.props.config?.defaultLimit &&
      this.props.config?.defaultLimit
    ) {
      this.setState({
        perPage: this.props.config.defaultLimit,
        currentPage: 1,
      });
    }

    const prevToken = _.get(prevProps, "user.accessToken", "");
    const accessToken = _.get(this.props, "user.accessToken", "");
    if (accessToken !== prevToken) this.fetchData();

    if (prevProps.fetching && this.props.fetched) {
      this.resetInternalState();
    }
  };

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateDimensions);
  }

  updateDimensions = () => {
    this.setState({ width: window.innerWidth });
  };

  resetInternalState = () => {
    this.setState({
      sortedData: null,
      sortedColumn: "",
    });
  };

  fetchData = (offset = 0) => {
    const { config, user, dispatch } = this.props;
    const accessToken = _.get(user, "accessToken", false);
    if (!accessToken) return;

    const filter = "all";
    const limit = this.state.perPage || config?.defaultLimit || 10;

    if (config.fetchAction) {
      dispatch(
        config.fetchAction(
          user.customerId,
          filter,
          user.accessToken,
          offset,
          limit,
        ),
      );
    }

    if (offset === 0 && config.fetchTotalAction) {
      dispatch(
        config.fetchTotalAction(user.customerId, filter, user.accessToken),
      );
    }
  };

  handlePageChange = (pageNumber) => {
    const offset = (pageNumber - 1) * this.state.perPage;
    this.setState({ currentPage: pageNumber, sortedData: null });
    this.fetchData(offset);
  };

  orderSort = (desc, field) => {
    const { config } = this.props;
    const dataList = this.props[config.dataKey] || [];

    let sorted = _.orderBy(
      dataList,
      (item) => {
        if (field === "bulkSubmissionArtifacts") {
          return (
            (item.bulkSubmissionArtifacts &&
              item.bulkSubmissionArtifacts[0] &&
              item.bulkSubmissionArtifacts[0].fileName) ||
            ""
          );
        }
        if (field === "processedSuccessfully") {
          return this.countEntitiesWithoutErrors(item);
        }
        if (field === "errorCount") {
          return this.getErrorCount(item);
        }
        return item[field];
      },
      desc ? "desc" : "asc",
    );

    this.setState({
      sortedData: sorted,
      sortedColumn: field,
    });
  };

  buildErrorRows = (bulkUpload) => {
    const entities =
      bulkUpload?.bulkSubmissionArtifacts[0]?.bulkSubmissionEntities;
    if (!entities || !Array.isArray(entities)) return [];

    const rows = [];

    entities.forEach((entity) => {
      const attempts = entity?.bulkSubmissionAttempts;
      if (!attempts || !Array.isArray(attempts)) return;

      attempts.forEach((attempt, attemptIndex) => {
        const invalidColumns = Array.isArray(attempt?.invalidColumnIndexes)
          ? attempt.invalidColumnIndexes
          : [];

        const responseCode =
          typeof attempt?.responseCode === "number"
            ? attempt.responseCode
            : null;
        const responseMessage =
          typeof attempt?.responseMessage === "string"
            ? attempt.responseMessage
            : "";

        const hasInvalidColumns = invalidColumns.length > 0;
        const hasBadResponseCode = responseCode != null && responseCode >= 400;
        const hasResponseMessage = !!responseMessage;

        if (!(hasInvalidColumns || hasBadResponseCode || hasResponseMessage)) {
          return;
        }

        let type = "Unknown";
        if (hasInvalidColumns) type = "Invalid columns";
        else if (hasBadResponseCode || hasResponseMessage) type = "API error";

        let message = "";
        if (hasResponseMessage) message = responseMessage;
        else if (hasInvalidColumns)
          message = `Invalid columns: ${invalidColumns.join(", ")}`;
        else message = "Unknown error";

        rows.push({
          rowNumber: entity?.rowNumber ?? "—",
          attempt: attemptIndex + 1,
          type,
          message,
          invalidColumns,
          responseCode,
        });
      });
    });

    return rows;
  };

  getErrorCount = (bulkUpload) => {
    return this.buildErrorRows(bulkUpload).length;
  };

  countEntitiesWithoutErrors = (bulkUpload) => {
    const entities =
      bulkUpload?.bulkSubmissionArtifacts[0]?.bulkSubmissionEntities;
    if (!entities || !Array.isArray(entities)) return "";

    const count = entities.filter((entity) => {
      const attempts = entity?.bulkSubmissionAttempts;
      if (!attempts || !Array.isArray(attempts)) return true;
      return attempts.every(
        (attempt) =>
          !Array.isArray(attempt?.invalidColumnIndexes) ||
          attempt.invalidColumnIndexes.length === 0,
      );
    }).length;

    return count > 0 ? count : "";
  };

  openErrorsModal = (bulkUpload) => {
    const rows = this.buildErrorRows(bulkUpload);
    const fileName = bulkUpload?.bulkSubmissionArtifacts?.[0]?.fileName || "";

    this.setState({
      showErrorsModal: true,
      errorsModalRows: rows,
      errorsModalFileName: fileName,
    });
  };

  closeErrorsModal = () => {
    this.setState({
      showErrorsModal: false,
      errorsModalRows: [],
      errorsModalFileName: "",
    });
  };

  handleDownload = async (artifactId, fileName) => {
    const { user, config } = this.props;
    const accessToken = _.get(user, "accessToken", false);
    if (!accessToken || !artifactId) return;

    let url;
    if (config.context === "inventory") {
      url = `${appConfig.inventoryApiUrl}/v1/transaction-reports-uploads/artifacts/${artifactId}`;
    } else if (config.context === "order") {
      url = `${appConfig.orderApiUrl}/v1/orders/artifacts/${artifactId}`;
    } else {
      console.warn("Unknown context for artifact download");
      return;
    }

    try {
      const response = await axios.get(url, {
        headers: requestHeaders(accessToken),
        responseType: "blob",
      });

      const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", fileName || `artifact-${artifactId}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading artifact:", error);
    }
  };

  render() {
    const { config, fetching, totalItems = 0 } = this.props;
    const loading = fetching;
    const propsDataList = this.props[config.dataKey] || [];
    const dataList = this.state.sortedData || propsDataList;
    const headers = this.state.headers;

    const perPage = this.state.perPage || config?.defaultLimit || 10;

    const paginatedList = Array.isArray(dataList)
      ? dataList.slice(0, perPage)
      : dataList;

    if (Array.isArray(dataList) && dataList.length > perPage) {
      console.warn(
        `[BatchProcessingHistory] API returned ${dataList.length} items, but limit was ${perPage}. ` +
          `Displaying only first ${perPage} items. This indicates the API may not be respecting the limit parameter.`,
      );
    }

    const itemsForPagination = new Array(
      Math.max(totalItems, paginatedList.length),
    ).fill(null);

    let paginator = Paginator.paginate(
      itemsForPagination,
      this.state.currentPage,
      perPage,
      this.state.width > 425 ? 6 : 1,
    );

    return (
      <div>
        <ConfirmationModal
          title={
            this.state.errorsModalFileName
              ? `Errors — ${this.state.errorsModalFileName}`
              : "Errors"
          }
          show={this.state.showErrorsModal}
          onClose={this.closeErrorsModal}
          size="4xl">
          <div className="tw-text-left tw-text-sm">
            <div className="tw-max-h-[60vh] tw-overflow-auto tw-border tw-border-gray-200 tw-rounded">
              <table className="tw-min-w-full tw-text-sm">
                <thead className="tw-sticky tw-top-0 tw-bg-gray-50">
                  <tr>
                    <th className="tw-px-3 tw-py-2 tw-border-b tw-text-left tw-text-xs tw-font-semibold tw-text-gray-900">
                      Row
                    </th>
                    <th className="tw-px-3 tw-py-2 tw-border-b tw-text-left tw-text-xs tw-font-semibold tw-text-gray-900">
                      Attempt
                    </th>
                    <th className="tw-px-3 tw-py-2 tw-border-b tw-text-left tw-text-xs tw-font-semibold tw-text-gray-900">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="tw-bg-white">
                  {this.state.errorsModalRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan="5"
                        className="tw-px-3 tw-py-4 tw-text-center tw-text-gray-600">
                        No errors to display.
                      </td>
                    </tr>
                  ) : (
                    this.state.errorsModalRows.map((r, idx) => (
                      <tr key={idx} className="hover:tw-bg-tosca-alice-blue">
                        <td className="tw-px-3 tw-py-2 tw-border-b">
                          {r.rowNumber}
                        </td>
                        <td className="tw-px-3 tw-py-2 tw-border-b">
                          {r.attempt}
                        </td>
                        <td className="tw-px-3 tw-py-2 tw-border-b tw-whitespace-normal tw-break-words">
                          {r.message}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </ConfirmationModal>
        <div>
          <div className="header-info">
            <div className="tw-flex tw-items-baseline">
              <h3>{config.title}</h3>
            </div>
          </div>
          <div className="tw-bg-white tw-px-2 tw-py-2">
            <div className="tw-flex tw-flex-col tw-bg-white">
              <div className="tw--my-2 tw-py-2 tw-overflow-x-auto tw-sm:-mx-6 tw-sm:px-6 tw-lg:-mx-8 tw-lg:px-8">
                <div className="tw-align-middle tw-inline-block tw-min-w-full tw-shadow tw-overflow-hidden tw-sm:rounded-lg tw-border-b tw-border-gray-200">
                  <table className="tw-min-w-full">
                    <thead>
                      <tr className="tw-bg-tosca-gray">
                        {headers.map((h, index) => (
                          <SortButton
                            key={index}
                            orderSort={this.orderSort}
                            sortField={h.field}
                            render={({ data }) => (
                              <th
                                onClick={data.clickHandler}
                                className="tw-px-3 tw-py-3 tw-border-b tw-border-gray-200 tw-bg-gray-50 tw-text-left tw-text-xs tw-leading-4 tw-font-medium tw-text-gray-900 tw-uppercase tw-tracking-wider tw-cursor-pointer hover:tw-bg-gray-300 tw-underline col-sort-header"
                                style={{
                                  position: "relative",
                                }}>
                                <div className="tw-w-3/4 tw-inline-block">
                                  {h.colTitle}
                                </div>
                                <div
                                  className="tw-w-1/4 tw-inline-block sort-arrow tw-text-right"
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    right: 3,
                                    transform: "translateY(-50%)",
                                  }}>
                                  {this.state.sortedColumn === h.field
                                    ? data.activeIcon
                                    : null}
                                </div>
                              </th>
                            )}
                          />
                        ))}
                      </tr>
                    </thead>
                    <tbody className="tw-bg-white">
                      {loading ? (
                        <tr>
                          <td colSpan="5">
                            <div className="tw-flex tw-items-center tw-justify-center tw-py-16- tw-text-gray-500 tw-font-light tw-text-xl">
                              <div className="loader-container tw-flex tw-justify-center tw-py-10">
                                <Loader
                                  type="Oval"
                                  color="rgba(246,130,32,1)"
                                  height="50"
                                  width="50"
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : paginatedList?.length === 0 ? (
                        <tr>
                          <td colSpan="5">
                            <div className="tw-flex tw-items-center tw-justify-center tw-py-16 tw-text-gray-500 tw-font-light tw-text-xl">
                              No records found.
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginatedList?.map((bulkUpload, index) => (
                          <React.Fragment key={index}>
                            <tr className="hover:tw-bg-tosca-alice-blue hover:tw-text-gray-900">
                              <td className="tw-px-3 tw-py-4 tw-border-b">
                                {moment(bulkUpload?.submissionTime).format(
                                  "ll",
                                )}
                              </td>
                              <td className="tw-px-3 tw-py-4 tw-border-b tw-whitespace-nowrap">
                                <span
                                  className="tw-text-tosca-orange tw-underline tw-cursor-pointer"
                                  onClick={() =>
                                    this.handleDownload(
                                      bulkUpload?.bulkSubmissionArtifacts[0]
                                        ?.bulkSubmissionArtifactId,
                                      bulkUpload?.bulkSubmissionArtifacts[0]
                                        ?.fileName,
                                    )
                                  }>
                                  {
                                    bulkUpload?.bulkSubmissionArtifacts[0]
                                      ?.fileName
                                  }
                                </span>
                              </td>
                              <td className="tw-px-3 tw-py-4 tw-border-b">
                                {
                                  bulkUpload?.bulkSubmissionArtifacts[0]
                                    ?.bulkSubmissionEntities?.length
                                }
                              </td>
                              <td className="tw-px-3 tw-py-4 tw-border-b">
                                {this.countEntitiesWithoutErrors(bulkUpload)}
                              </td>
                              <td className="tw-px-3 tw-py-4 tw-border-b">
                                {this.getErrorCount(bulkUpload) > 0 ? (
                                  <span
                                    className="tw-text-tosca-orange tw-underline tw-cursor-pointer"
                                    onClick={() =>
                                      this.openErrorsModal(bulkUpload)
                                    }>
                                    {this.getErrorCount(bulkUpload)}
                                  </span>
                                ) : null}
                              </td>
                            </tr>
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div
              className="pagination-comp tw-flex tw-justify-center"
              style={{
                display: loading || paginatedList.length === 0 ? "none" : "",
              }}>
              {paginator.links(this.handlePageChange)}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default BatchProcessingHistory;
